"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var rules_api_1 = require("@gamepark/rules-api");
var assert_1 = __importDefault(require("assert"));
var fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
var immer_1 = __importDefault(require("immer"));
var ActionFailed_1 = require("../Actions/ActionFailed");
var AnimationBreakComplete_1 = require("../Actions/AnimationBreakComplete");
var AnimationComplete_1 = require("../Actions/AnimationComplete");
var ClearFailures_1 = require("../Actions/ClearFailures");
var GameNotificationsReceived_1 = require("../Actions/GameNotificationsReceived");
var GameOver_1 = require("../Actions/GameOver");
var GameStateLoaded_1 = require("../Actions/GameStateLoaded");
var IgnoreRematch_1 = require("../Actions/IgnoreRematch");
var MovePlayed_1 = require("../Actions/MovePlayed");
var MoveUndone_1 = require("../Actions/MoveUndone");
var SetAnimationsSpeed_1 = require("../Actions/SetAnimationsSpeed");
var StartRematch_1 = require("../Actions/StartRematch");
var GiveUp_1 = require("../GiveUp/GiveUp");
var index_1 = require("../index");
var MuteSounds_1 = require("../Sound/MuteSounds");
var UnmuteSounds_1 = require("../Sound/UnmuteSounds");
var MovePlayedNotification_1 = require("../Types/MovePlayedNotification");
var MoveUndoneNotification_1 = require("../Types/MoveUndoneNotification");
var RematchNotification_1 = require("../Types/RematchNotification");
var ArrayUtil_1 = require("../util/ArrayUtil");
function gameReducer(gameName, GameCreator, animations) {
    var initialState = {
        players: [], pendingNotifications: [], nextNotificationIndex: 0, failures: [], animationIncrement: 0, animationsSpeed: 1,
        soundsMuted: MuteSounds_1.soundsMuted(), clientTimeDelta: 0
    };
    return function (state, event) {
        if (state === void 0) { state = initialState; }
        switch (event.type) {
            case GameStateLoaded_1.GAME_STATE_LOADED:
                return immer_1.default(state, function (draft) {
                    if (event.serverTime) {
                        draft.clientTimeDelta = approximateTimeDelta(event.serverTime, draft.clientTimeDelta);
                    }
                    draft.players = event.players;
                    draft.options = event.options;
                    draft.playerId = event.playerId;
                    draft.state = event.state;
                    draft.setup = event.setup;
                    draft.gameOver = !!event.endDate;
                    draft.gameMode = event.mode;
                    draft.nextNotificationIndex = event.notificationsCount;
                    draft.pendingNotifications = draft.pendingNotifications.filter(function (notification) { return notification.index >= draft.nextNotificationIndex; });
                });
            case GameNotificationsReceived_1.GAME_NOTIFICATIONS_RECEIVED:
                return immer_1.default(state, function (draft) {
                    if (event.serverTime) {
                        draft.clientTimeDelta = approximateTimeDelta(event.serverTime, draft.clientTimeDelta);
                    }
                    draft.players.forEach(function (player) {
                        var _a;
                        var newPlayerTime = (_a = event.players.find(function (p) { return p.id === player.id; })) === null || _a === void 0 ? void 0 : _a.time;
                        player.time = newPlayerTime || player.time;
                    });
                    if (event.notificationsIndex == 0 && !draft.actions) {
                        setActionsFromPastNotifications(draft, event.notifications);
                    }
                    for (var i = Math.max(draft.nextNotificationIndex - event.notificationsIndex, 0); i < event.notifications.length; i++) {
                        draft.pendingNotifications.push(__assign(__assign({}, event.notifications[i]), { index: i + event.notificationsIndex }));
                    }
                    draft.pendingNotifications.sort(function (n1, n2) { return n1.index - n2.index; });
                    if (draft.state) {
                        applyPendingNotifications(draft);
                    }
                });
            case MovePlayed_1.MOVE_PLAYED:
                return immer_1.default(state, function (draft) {
                    if (!draft.actions || !draft.state) {
                        throw new Error('Cannot play move before game history is loaded');
                    }
                    if (event.local) {
                        var game_1 = new GameCreator(draft.state);
                        game_1.play(JSON.parse(JSON.stringify(event.move)));
                        return;
                    }
                    if (draft.playerId === undefined)
                        throw new Error('Spectators cannot play');
                    if (event.delayed) {
                        draft.actions.push({ playerId: draft.playerId, move: event.move, consequences: [], pending: true, delayed: true });
                        return;
                    }
                    var game = getGameAfterAnimations(state);
                    var action = rules_api_1.playAction(game, event.move, state.playerId);
                    var displayedAction = __assign(__assign({}, action), { pending: true, delayed: false });
                    if (event.skipAnimation) {
                        displayedAction.animation = { id: draft.animationIncrement++, duration: 0 };
                    }
                    draft.actions.push(displayedAction);
                    animateAction(displayedAction, draft);
                });
            case MoveUndone_1.MOVE_UNDONE:
                return immer_1.default(state, function (draft) {
                    if (!state.actions || !draft.actions || !state.setup) {
                        return console.error('Cannot undo before actions history is loaded');
                    }
                    var action = draft.actions.filter(function (action) { return action.playerId === state.playerId && !action.cancelled; })[event.playerActionIndex];
                    if (!action) {
                        return console.error('Could not find action to undo');
                    }
                    var i = draft.actions.indexOf(action);
                    var consecutiveActions = draft.actions.slice(i + 1).filter(function (action) { return !action.cancelled; });
                    var game = new GameCreator(JSON.parse(JSON.stringify(state.setup)));
                    rules_api_1.replayActions(game, state.actions.filter(function (action) { return !action.delayed && !action.cancelled; }));
                    if (!rules_api_1.hasUndo(game) || !game.canUndo(action, consecutiveActions)) {
                        return console.error('Try to undo an action which is not allowed to undo');
                    }
                    action.cancelled = true;
                    action.cancelPending = true;
                    if (state.playerId !== undefined && !event.delayed && !action.animation && !action.delayed) {
                        // Assume that if it is our turn to play, the UNDO will not fail so it can be applied immediately
                        animateAction(action, draft);
                    }
                });
            case ActionFailed_1.ACTION_FAILED:
                return immer_1.default(state, function (draft) {
                    if (draft.actions) {
                        draft.actions = draft.actions.filter(function (action) { return !action.pending; });
                        draft.actions.forEach(function (action) {
                            if (action.cancelPending) {
                                action.cancelled = false;
                                action.cancelPending = false;
                            }
                            action.animation = undefined;
                        });
                        var game = new GameCreator(JSON.parse(JSON.stringify(draft.setup)));
                        rules_api_1.replayActions(game, draft.actions);
                        draft.state = game.state;
                    }
                    draft.failures.push(event.reason);
                });
            case ClearFailures_1.CLEAR_FAILURES:
                return immer_1.default(state, function (draft) {
                    draft.failures = [];
                });
            case AnimationComplete_1.ANIMATION_COMPLETE:
                return immer_1.default(state, function (draft) {
                    if (draft.actions) {
                        var action = draft.actions.find(function (action) { var _a; return ((_a = action.animation) === null || _a === void 0 ? void 0 : _a.id) === event.animationId; });
                        if (!action) {
                            return console.error('ANIMATION_COMPLETE event received but not animation found with provided id');
                        }
                        animateAction(action, draft);
                        if (!draft.actions.some(function (action) { return action.animation; })) {
                            draft.animationsBreak = true;
                        }
                    }
                });
            case AnimationBreakComplete_1.ANIMATION_BREAK_COMPLETE:
                return immer_1.default(state, function (draft) {
                    delete draft.animationsBreak;
                    if (draft.actions && !draft.actions.some(function (action) { return action.animation; })) {
                        animateNextAction(draft);
                    }
                });
            case SetAnimationsSpeed_1.SET_ANIMATIONS_SPEED:
                return __assign(__assign({}, state), { animationsSpeed: event.speed });
            case StartRematch_1.START_REMATCH:
                if (state.playerId === undefined) {
                    throw new Error('Spectators cannot offer a rematch');
                }
                if (event.rematchNotification) {
                    return __assign(__assign({}, state), { rematchOffer: {
                            playerId: event.rematchNotification.playerId,
                            link: index_1.PLATFORM_URI + '/?game=' + gameName + '&id=' + event.rematchNotification.rematchId
                        } });
                }
                else {
                    return __assign(__assign({}, state), { rematchOffer: { playerId: state.playerId } });
                }
            case IgnoreRematch_1.IGNORE_REMATCH:
                return __assign(__assign({}, state), { rematchOffer: undefined });
            case GiveUp_1.GIVE_UP:
                return GiveUp_1.giveUp(state);
            case MuteSounds_1.MUTE_SOUNDS:
                return __assign(__assign({}, state), { soundsMuted: true });
            case UnmuteSounds_1.UNMUTE_SOUNDS:
                return __assign(__assign({}, state), { soundsMuted: false });
            case GameOver_1.GAME_OVER:
                return __assign(__assign({}, state), { gameOver: true, players: state.players.map(function (player) {
                        var p = event.players.find(function (p) { return p.id === player.id; });
                        if (!p)
                            return player;
                        else
                            return (__assign(__assign({}, player), { gamePointsDelta: p.gamePointsDelta }));
                    }) });
        }
        return state;
    };
    function setActionsFromPastNotifications(draft, notifications) {
        draft.actions = [];
        for (var i = 0; i < draft.nextNotificationIndex; i++) {
            var notification = notifications[i];
            if (MovePlayedNotification_1.isMovePlayedNotification(notification)) {
                draft.actions.push({
                    playerId: notification.playerId, move: notification.moveView,
                    consequences: notification.consequences
                });
            }
            else if (MoveUndoneNotification_1.isMoveUndoneNotification(notification)) {
                draft.actions.splice(notification.moveUndone, 1);
            }
        }
    }
    function getGameAfterAnimations(state) {
        if (!state.actions || !state.setup) {
            throw new Error('Cannot play move before game history is loaded');
        }
        if (state.actions.some(function (action) { return !action.delayed && !action.cancelled; })) {
            // We need to avoid side effects of other actions that we might currently be animating => let's get the game state as if every animation is over
            var game = new GameCreator(JSON.parse(JSON.stringify(state.setup)));
            rules_api_1.replayActions(game, state.actions.filter(function (action) { return !action.delayed && !action.cancelled; }));
            return game;
        }
        else {
            return new GameCreator(JSON.parse(JSON.stringify(state.state)));
        }
    }
    function applyPendingNotifications(draft) {
        if (!draft.actions) {
            return;
        }
        while (draft.pendingNotifications.length && draft.pendingNotifications[0].index <= draft.nextNotificationIndex) {
            var notification = draft.pendingNotifications.shift();
            if (notification.index < draft.nextNotificationIndex) {
                console.info('A notification was received twice: ' + JSON.stringify(notification));
            }
            else {
                draft.nextNotificationIndex++;
                applyNotification(draft, notification);
            }
        }
        if (!draft.actions.some(function (action) { return action.animation; })) {
            animateNextAction(draft);
        }
    }
    function applyNotification(draft, notification) {
        if (MovePlayedNotification_1.isMovePlayedNotification(notification)) {
            applyMovePlayedNotification(draft, notification);
        }
        else if (MoveUndoneNotification_1.isMoveUndoneNotification(notification)) {
            applyMoveUndoneNotification(draft, notification);
        }
        else if (RematchNotification_1.isRematchNotification(notification)) {
            draft.rematchOffer = { playerId: notification.playerId, link: index_1.PLATFORM_URI + '/?game=' + gameName + '&id=' + notification.rematchId };
        }
    }
    function applyMovePlayedNotification(draft, notification) {
        var _a;
        assert_1.default(draft.actions !== undefined, 'Cannot apply a notification before action history is loaded');
        var action;
        if (notification.playerId == draft.playerId) {
            action = draft.actions.find(function (action) { return action.pending && action.playerId == notification.playerId; });
        }
        if (action) {
            action.pending = false;
            if (action.delayed) {
                action.move = notification.moveView;
                action.consequences = notification.consequences;
            }
            else {
                // Help for developer: if action is not delayed, its data must be equal to the notification's data
                if (!fast_deep_equal_1.default(JSON.parse(JSON.stringify(action.move)), JSON.parse(JSON.stringify(notification.moveView)))) {
                    console.error('An action was played an executed immediately:', JSON.parse(JSON.stringify(action.move)), ', but the notifications does not contain the same data:', JSON.parse(JSON.stringify(notification.moveView)), '(use the "delayed" parameter to postpone execution if the outcome cannot be anticipated).');
                }
                for (var i = 0; i < action.consequences.length || i < notification.consequences.length; i++) {
                    if (notification.consequences.length - 1 < i) {
                        console.error('A consequence was played locally but it was missing in the notification:', JSON.parse(JSON.stringify(action.consequences[i])));
                    }
                    else if (action.consequences.length - 1 < i) {
                        action.consequences.push(notification.consequences[i]);
                        if (!action.animation) {
                            // Start animating the new consequences discovered
                            var duration = getAnimationDuration(action.consequences[i], action, draft) / draft.animationsSpeed;
                            action.animation = { id: draft.animationIncrement++, consequence: i, duration: duration };
                        }
                    }
                    else if (!fast_deep_equal_1.default(JSON.parse(JSON.stringify(action.consequences[i])), JSON.parse(JSON.stringify(notification.consequences[i])))) {
                        console.error('A consequence of an action was played and executed immediately:', JSON.parse(JSON.stringify(action.consequences[i])), ', but the notifications does not contain the same data:', JSON.parse(JSON.stringify(notification.consequences[i])), '(the problem is inside function "getAutomaticMoves": when it is executed on the client side, it must return null if the next consequence cannot be predicted by the client).');
                    }
                    if (((_a = action.animation) === null || _a === void 0 ? void 0 : _a.duration) === 0) {
                        animateAction(action, draft);
                    }
                }
            }
        }
        else {
            draft.actions.splice(ArrayUtil_1.findLastIndex(draft.actions, function (action) { return !action.pending; }) + 1, 0, {
                playerId: notification.playerId, move: notification.moveView,
                consequences: notification.consequences,
                delayed: true
            });
        }
    }
    function applyMoveUndoneNotification(draft, notification) {
        assert_1.default(draft.actions !== undefined, 'Cannot apply a notification before action history is loaded');
        // draft.actions! <--- ! can be removed bellow once this issue is resolved: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/44117
        var actionIndex = notification.moveUndone;
        for (var i = 0; i < notification.moveUndone; i++) {
            if (draft.actions[i].cancelled && !draft.actions[i].cancelPending) {
                actionIndex++; // Server sends the index considering that previously cancelled actions are no longer in the list. We may still be animating them on client side.
            }
        }
        var cancelledAction = draft.actions[actionIndex];
        if (cancelledAction.delayed) {
            // We can immediately remove it
            draft.actions.splice(actionIndex, 1);
        }
        else {
            cancelledAction.cancelled = true;
            cancelledAction.cancelPending = false;
        }
    }
    function animateNextAction(draft) {
        if (!draft.actions || draft.animationsBreak) {
            return;
        }
        var action = draft.actions.find(function (action) { return (action.delayed && !action.pending && !action.cancelled) || (action.cancelled && !action.cancelPending && !action.delayed); });
        if (action) {
            animateAction(action, draft);
            if (!action.animation) {
                animateNextAction(draft);
            }
        }
        else {
            // Clean action that are completely undone
            draft.actions = draft.actions.filter(function (action) { return !action.cancelled || !action.delayed || action.cancelPending; });
        }
    }
    function animateAction(action, draft) {
        if (!action.cancelled) {
            animatePlayedAction(action, draft);
        }
        else {
            animateUndoneAction(action, draft);
        }
        if (action.animation && action.animation.duration === 0) {
            // If there is no animation defined for this step
            animateAction(action, draft);
        }
    }
    function animatePlayedAction(action, draft) {
        if (!action.animation) {
            action.delayed = false;
            var duration = getAnimationDuration(action.move, action, draft) / draft.animationsSpeed;
            action.animation = { id: draft.animationIncrement++, duration: duration };
        }
        else {
            var move = action.animation.consequence === undefined ? action.move : action.consequences[action.animation.consequence];
            var game = new GameCreator(draft.state);
            game.play(JSON.parse(JSON.stringify(move)));
            if (action.consequences.length > 0 && (action.animation.consequence === undefined || action.animation.consequence < action.consequences.length - 1)) {
                var nextConsequence = action.animation.consequence === undefined ? 0 : action.animation.consequence + 1;
                var duration = getAnimationDuration(action.consequences[nextConsequence], action, draft) / draft.animationsSpeed;
                action.animation = { id: draft.animationIncrement++, consequence: nextConsequence, duration: duration };
            }
            else {
                delete action.animation;
            }
        }
    }
    function animateUndoneAction(action, draft) {
        if (!action.animation) {
            if (action.consequences.length > 0) {
                // Start by undoing the last consequence of the action
                var move = action.consequences[action.consequences.length - 1];
                var duration = getUndoAnimationDuration(move, action, draft) / draft.animationsSpeed;
                action.animation = { id: draft.animationIncrement++, duration: duration, consequence: action.consequences.length - 1 };
            }
            else {
                var duration = getUndoAnimationDuration(action.move, action, draft) / draft.animationsSpeed;
                action.animation = { id: draft.animationIncrement++, duration: duration };
            }
        }
        else if (action.animation.consequence !== undefined) {
            if (action.animation.consequence > 0) {
                var previousConsequence = action.animation.consequence - 1;
                var move = action.consequences[previousConsequence];
                var duration = getUndoAnimationDuration(move, action, draft) / draft.animationsSpeed;
                action.animation = { id: draft.animationIncrement++, consequence: previousConsequence, duration: duration };
            }
            else {
                var duration = getUndoAnimationDuration(action.move, action, draft) / draft.animationsSpeed;
                action.animation = { id: draft.animationIncrement++, duration: duration };
            }
        }
        else {
            delete action.animation;
            action.delayed = true; // Action is now delayed (not executed) and cancelled: once pending is false too, it will be removed.
        }
        var game = new GameCreator(JSON.parse(JSON.stringify(draft.setup)));
        var actionsPlayed = draft.actions.filter(function (action) { return !action.delayed && !action.cancelled && (!action.animation || action.animation.consequence !== undefined); })
            .map(function (action) { return ({
            playerId: action.playerId,
            move: action.move,
            consequences: (action.animation ? action.consequences.filter(function (_, index) { return index < action.animation.consequence; }) : action.consequences)
        }); });
        rules_api_1.replayActions(game, actionsPlayed);
        if (rules_api_1.hasUndo(game) && game.restoreLocalMoves && draft.state) {
            game.restoreLocalMoves(draft.state);
        }
        draft.state = game.state;
    }
    function getAnimationContext(action, draft) {
        return {
            action: action, state: draft.state, playerId: draft.playerId
        };
    }
    function getAnimationDuration(move, action, draft) {
        if (!animations) {
            return 0;
        }
        return animations.getAnimationDuration(move, getAnimationContext(action, draft));
    }
    function getUndoAnimationDuration(move, action, draft) {
        if (!animations || !animations.getUndoAnimationDuration) {
            return 0;
        }
        return animations.getUndoAnimationDuration(move, getAnimationContext(action, draft));
    }
}
exports.default = gameReducer;
var estimatedLatency = 100;
function approximateTimeDelta(serverTime, clientTimeDelta) {
    return Math.min(serverTime + estimatedLatency - new Date().getTime(), clientTimeDelta || Infinity);
}
//# sourceMappingURL=gameReducer.js.map